/**
 * capacityUtils
 * --------------
 * Shared helpers for normalizing rage room capacity math so booking
 * flows can respond consistently even when slots are blocked.
 */
const sumSlotCapacities = (cell) => {
  if (!cell) {
    return 0;
  }
  if (typeof cell.totalCapacity === 'number') {
    return cell.totalCapacity;
  }
  if (Array.isArray(cell.slots) && cell.slots.length) {
    return cell.slots.reduce((sum, slot) => sum + (slot?.maxCapacity || 0), 0);
  }
  return 0;
};

const resolveBookedCount = (cell) => {
  if (!cell) {
    return 0;
  }
  if (typeof cell.bookedCount === 'number') {
    return cell.bookedCount;
  }
  if (Array.isArray(cell.bookings) && cell.bookings.length) {
    return cell.bookings.reduce(
      (sum, booking) => sum + (booking?.partySize ?? booking?.slotQuantity ?? 1),
      0
    );
  }
  return 0;
};

export const getEffectiveRemainingCapacity = (cell) => {
  if (!cell) {
    return null;
  }

  const totalCapacity = sumSlotCapacities(cell);
  const bookedCount = resolveBookedCount(cell);

  if (cell.isBlocked) {
    return Math.max(totalCapacity - bookedCount, 0);
  }

  if (typeof cell.remainingCapacity === 'number') {
    return Math.max(cell.remainingCapacity, 0);
  }

  if (!totalCapacity) {
    return null;
  }

  return Math.max(totalCapacity - bookedCount, 0);
};

export const getEditableMaxPartySize = (cell, booking) => {
  const effectiveRemaining = getEffectiveRemainingCapacity(cell);

  if (!booking) {
    return effectiveRemaining;
  }

  if (effectiveRemaining === null) {
    return booking.partySize ?? booking.slotQuantity ?? null;
  }

  const bookingPartySize = booking.partySize ?? booking.slotQuantity ?? 1;
  const totalCapacity = sumSlotCapacities(cell) || bookingPartySize;
  const capacityWithCurrent = effectiveRemaining + bookingPartySize;

  if (!totalCapacity) {
    return Math.max(capacityWithCurrent, bookingPartySize);
  }

  return Math.max(Math.min(capacityWithCurrent, totalCapacity), bookingPartySize);
};